(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hanezza-brand-reviews]';
  const FALLBACK_SELECTOR = '[data-hanezza-air-data-fallback]';
  const MAX_WAIT_TICKS = 20;

  const text = (value = '') => String(value ?? '').trim();

  function el(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined && value !== null) node.textContent = text(value);
    return node;
  }

  function parseDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  function reviewRate(review) {
    const rate = Number(review?.rate);
    return Number.isFinite(rate) ? Math.max(0, Math.min(5, rate)) : 0;
  }

  function getReviews(root) {
    const reviews = window.AIR_REVIEWS?.productReview?.reviews;
    if (!Array.isArray(reviews)) return [];

    const productId = String(root?.dataset.productId || '');
    return reviews.filter((review) => {
      const approved = !review?.status || review.status === 'approved';
      const sameProduct = !productId || !review?.productId || String(review.productId) === productId;
      return approved && sameProduct && text(review?.content);
    });
  }

  function nativeWidgetReady(host) {
    if (!host) return false;
    if (host.querySelector('iframe')) return true;
    if (Array.from(host.querySelectorAll('*')).some((node) => node.shadowRoot)) return true;

    return Array.from(host.querySelectorAll('.hanezza-brand-reviews__app-block *')).some((node) => {
      if (node.closest(FALLBACK_SELECTOR)) return false;
      if (node.matches('input[type="hidden"], script, style, template')) return false;
      if (node.matches('img, video, button, select, textarea, form, article, [role="article"], [role="listitem"]')) return true;
      if (node.children.length > 0) return false;
      return text(node.textContent).length > 20;
    });
  }

  function extractUrls(value, output = new Set(), depth = 0) {
    if (depth > 5 || value == null) return output;

    if (typeof value === 'string') {
      const candidate = value.trim();
      if (/^(https?:)?\/\//i.test(candidate)) output.add(candidate.startsWith('//') ? `https:${candidate}` : candidate);
      else if ((candidate.startsWith('[') || candidate.startsWith('{')) && candidate.length < 20000) {
        try { extractUrls(JSON.parse(candidate), output, depth + 1); } catch (_error) { /* Ignore non-JSON strings. */ }
      }
      return output;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => extractUrls(item, output, depth + 1));
      return output;
    }

    if (typeof value === 'object') {
      ['url', 'src', 'imageUrl', 'videoUrl', 'originalUrl', 'previewImage'].forEach((key) => {
        if (key in value) extractUrls(value[key], output, depth + 1);
      });
    }

    return output;
  }

  function replyText(review) {
    const reply = review?.shopReply;
    if (!reply || typeof reply !== 'object') return '';
    return text(reply.content || reply.message || reply.reply || reply.text || reply.body);
  }

  function buildMedia(review) {
    const urls = Array.from(extractUrls([review?.images, review?.videos])).slice(0, 8);
    if (!urls.length) return null;

    const media = el('div', 'hanezza-air-data__media');
    urls.forEach((url) => {
      const isVideo = /\.(mp4|webm|mov)(?:\?|$)/i.test(url);
      if (isVideo) {
        const video = document.createElement('video');
        video.src = url;
        video.controls = true;
        video.preload = 'metadata';
        media.appendChild(video);
      } else {
        const image = document.createElement('img');
        image.src = url;
        image.loading = 'lazy';
        image.alt = 'Müşteri değerlendirmesi görseli';
        media.appendChild(image);
      }
    });
    return media;
  }

  function buildCard(review) {
    const card = el('article', 'hanezza-air-review-card hanezza-air-data__card');
    card.setAttribute('role', 'article');

    const header = el('div', 'hanezza-air-data__card-header');
    const identity = el('div', 'hanezza-air-data__identity');
    identity.appendChild(el('strong', 'hanezza-air-data__name', review?.firstName || 'Müşteri'));

    if (review?.verified || review?.verifyBuyer) {
      identity.appendChild(el('span', 'hanezza-air-data__verified', 'Doğrulanmış değerlendirme'));
    }

    const meta = el('div', 'hanezza-air-data__meta');
    const stars = el('span', 'hanezza-air-data__stars', '★★★★★');
    const rate = reviewRate(review);
    stars.setAttribute('aria-label', `5 üzerinden ${rate} yıldız`);
    stars.style.setProperty('--hanezza-review-rate', `${(rate / 5) * 100}%`);
    meta.appendChild(stars);

    const formattedDate = parseDate(review?.createdAt);
    if (formattedDate) meta.appendChild(el('time', 'hanezza-air-data__date', formattedDate));

    header.append(identity, meta);
    card.appendChild(header);
    card.appendChild(el('p', 'hanezza-air-data__content', review?.content));

    const media = buildMedia(review);
    if (media) card.appendChild(media);

    const response = replyText(review);
    if (response) {
      const responseBox = el('div', 'hanezza-air-merchant-response hanezza-air-data__reply');
      responseBox.appendChild(el('strong', '', 'Mağaza yanıtladı'));
      responseBox.appendChild(el('p', '', response));
      card.appendChild(responseBox);
    }

    return card;
  }

  function renderCards(list, reviews, sortValue) {
    const sorted = [...reviews].sort((a, b) => {
      if (sortValue === 'highest') return reviewRate(b) - reviewRate(a);
      if (sortValue === 'lowest') return reviewRate(a) - reviewRate(b);
      return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
    });

    list.replaceChildren(...sorted.map(buildCard));
  }

  function updateSummary(root, reviews) {
    if (!reviews.length) return;
    const average = reviews.reduce((sum, review) => sum + reviewRate(review), 0) / reviews.length;
    const fill = root.querySelector('[data-review-stars-fill]');
    const ratingText = root.querySelector('[data-review-rating-text]');
    const countText = root.querySelector('[data-review-count-text]');
    const stars = root.querySelector('[data-review-stars]');

    if (fill) fill.style.width = `${(average / 5) * 100}%`;
    if (ratingText) {
      ratingText.hidden = false;
      ratingText.textContent = `${average.toFixed(1).replace('.', ',')} / 5`;
    }
    if (countText) countText.textContent = `${reviews.length} müşteri değerlendirmesi`;
    if (stars) stars.setAttribute('aria-label', `5 üzerinden ${average.toFixed(1)} yıldız`);
  }

  function activateFallback(root, reviews) {
    updateSummary(root, reviews);
    root.classList.add('is-air-integrated', 'is-air-data-fallback');
    root.dataset.widgetMode = 'air-data';
    root.dataset.widgetReady = 'true';
  }

  function createFallback(root, host, reviews) {
    const container = el('section', 'hanezza-air-data-fallback');
    container.dataset.hanezzaAirDataFallback = 'true';
    container.setAttribute('aria-label', 'Müşteri değerlendirmeleri');

    const toolbar = el('div', 'hanezza-air-data__toolbar');
    toolbar.appendChild(el('strong', 'hanezza-air-data__total', `${reviews.length} değerlendirme`));

    const sort = document.createElement('select');
    sort.className = 'hanezza-air-control hanezza-air-data__sort';
    sort.setAttribute('aria-label', 'Değerlendirmeleri sırala');
    [
      ['recent', 'En yeni'],
      ['highest', 'En yüksek puan'],
      ['lowest', 'En düşük puan']
    ].forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      sort.appendChild(option);
    });
    toolbar.appendChild(sort);

    const list = el('div', 'hanezza-air-review-list hanezza-air-data__list');
    sort.addEventListener('change', () => renderCards(list, reviews, sort.value));
    renderCards(list, reviews, 'recent');

    container.append(toolbar, list);
    host.appendChild(container);
    activateFallback(root, reviews);
  }

  function reconcile(root) {
    const host = root?.querySelector('[data-brand-review-widget]');
    if (!host) return false;

    const existing = host.querySelector(FALLBACK_SELECTOR);
    if (nativeWidgetReady(host)) {
      existing?.remove();
      root.classList.remove('is-air-data-fallback');
      return true;
    }

    const reviews = getReviews(root);
    if (!reviews.length) return false;
    if (!existing) createFallback(root, host, reviews);
    else activateFallback(root, reviews);
    return true;
  }

  function mount(root) {
    if (!root || root.dataset.hanezzaAirDataMounted === 'true') return;
    root.dataset.hanezzaAirDataMounted = 'true';

    let ticks = 0;
    let timer = 0;
    const check = () => {
      reconcile(root);
      ticks += 1;
      if (ticks >= MAX_WAIT_TICKS && timer) window.clearInterval(timer);
    };

    check();
    timer = window.setInterval(check, 500);

    const host = root.querySelector('[data-brand-review-widget]');
    if (host) {
      let frame = 0;
      const observer = new MutationObserver(() => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => reconcile(root));
      });
      observer.observe(host, { childList: true, subtree: true });
      root.hanezzaAirDataObserver = observer;
    }
  }

  function boot(scope = document) {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => root.hanezzaAirDataObserver?.disconnect());
  });
})();